import { StatusPill } from "@/components/status-pill";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Boxes,
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  PackagePlus,
  Plus,
  Trash2,
} from "lucide-react";
import type {
  DropshipV2Issue,
  DropshipV2Line,
  DropshipV2LineChanges,
  DropshipV2LineGroup,
} from "@/api/dropship-v2";
import {
  DropshipLineActions,
  DropshipLineColumnHeaders,
  DropshipLineControlBand,
  DropshipLineDataGrid,
  DropshipLineNumericGrid,
  DropshipLineRowShell,
  DropshipLineValue,
} from "@/components/dropship-line-item-layout";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { POLineItemProductCell } from "@/components/po-line-item-product-cell";
import { ProductDetailsDialogTrigger } from "@/components/product-details-dialog";
import { ActionPopover } from "@/components/action-popover";
import { QuoteAgentLineActionIcon } from "@/components/dropship-quote-agent";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { ChoicePicker } from "@/components/choice-picker";
import { cn, formatMoney, formatStatus } from "@/lib/utils";
import { groupByManufacturer } from "@/features/dropship/projections";
import type {
  DropshipLineSortMode,
  DropshipManufacturerBucketSummary,
} from "@/features/dropship/types";

export type {
  DropshipLineSortMode,
  DropshipManufacturerBucketSummary,
} from "@/features/dropship/types";

function amount(value: string | null | undefined) {
  return value == null || value === "" ? "—" : formatMoney(String(value));
}

function editableValue(
  field: keyof DropshipV2LineChanges,
  rawValue: string,
): DropshipV2LineChanges {
  return { [field]: rawValue.trim() || null } as DropshipV2LineChanges;
}

type LineKind = NonNullable<DropshipV2LineChanges["line_kind"]>;
type LineDisposition = NonNullable<DropshipV2LineChanges["disposition"]>;

const LINE_KIND_OPTIONS: ReadonlyArray<{ value: LineKind; label: string }> = [
  { value: "configuration", label: "Configuration" },
  { value: "accessory", label: "Accessory" },
  { value: "warranty", label: "Warranty" },
  { value: "informational", label: "Informational" },
];

const LINE_DISPOSITION_OPTIONS: ReadonlyArray<{
  value: LineDisposition;
  label: string;
}> = [
  { value: "include_on_po", label: "Included on PO" },
  { value: "internal_only", label: "Manual" },
];

const LEGACY_LINE_DISPOSITION_LABELS: Partial<Record<LineDisposition, string>> =
  {
    excluded_with_reason: "Excluded",
    unresolved: "Needs review",
  };

function LineChoiceMenu<T extends string>({
  value,
  options,
  ariaLabel,
  busy,
  onChange,
  fallbackLabels,
  warning = false,
  align = "start",
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  ariaLabel: string;
  busy: boolean;
  onChange: (value: T) => void;
  fallbackLabels?: Partial<Record<T, string>>;
  warning?: boolean;
  align?: "start" | "end";
}) {
  const selected = options.find((option) => option.value === value);
  const selectedLabel =
    selected?.label ?? fallbackLabels?.[value] ?? formatStatus(value);
  return (
    <ChoicePicker
      title={ariaLabel}
      label={`${ariaLabel}: ${selectedLabel}`}
      value={value}
      choices={options}
      onSelect={(nextValue) => onChange(nextValue as T)}
      disabled={busy}
      align={align}
      menuClassName="w-auto min-w-44"
      buttonTrigger
    >
      <button
        type="button"
        aria-label={`${ariaLabel}: ${selectedLabel}`}
        className={cn(
          "inline-flex min-h-11 max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60 @4xl/dropship-lines:min-h-7",
          warning &&
            "border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200",
        )}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
      </button>
    </ChoicePicker>
  );
}

function LineChoiceValue<T extends string>({
  value,
  options,
  ariaLabel,
  fallbackLabels,
  warning = false,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  ariaLabel: string;
  fallbackLabels?: Partial<Record<T, string>>;
  warning?: boolean;
}) {
  const selected = options.find((option) => option.value === value);
  const selectedLabel =
    selected?.label ?? fallbackLabels?.[value] ?? formatStatus(value);
  return (
    <Badge
      variant="outline"
      aria-label={`${ariaLabel}: ${selectedLabel}`}
      className={cn(
        "max-w-full truncate",
        warning &&
          "border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200",
      )}
    >
      {selectedLabel}
    </Badge>
  );
}

function PriceEditor({
  label,
  value,
  displayValue,
  ariaLabel,
  busy,
  editable = true,
  kind = "money",
  priority = "normal",
  blocker,
  onConfirm,
}: {
  label: string;
  value: string | null | undefined;
  displayValue: string;
  ariaLabel: string;
  busy: boolean;
  editable?: boolean;
  kind?: "money" | "text";
  priority?: "primary" | "normal" | "secondary";
  blocker?: string;
  onConfirm: (value: string) => void;
}) {
  const invalid = Boolean(blocker);
  return (
    <DropshipLineValue
      label={label}
      className={cn(
        priority === "primary" && "font-semibold text-foreground",
        priority === "secondary" && "text-muted-foreground",
      )}
    >
      <BlockerTooltip message={blocker}>
        {editable ? (
          <POInlineEditableField
            value={value}
            displayValue={displayValue}
            placeholder="—"
            kind={kind}
            align="right"
            ariaLabel={ariaLabel}
            title={invalid ? "" : undefined}
            pending={busy}
            invalid={invalid}
            buttonClassName={cn(
              "ml-auto min-h-11 min-w-0 w-full justify-end px-1 text-right text-sm tabular-nums @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:text-xs",
              priority === "primary" && "font-semibold text-foreground",
              priority === "secondary" && "text-muted-foreground",
              invalid && "text-destructive",
            )}
            inputClassName="w-full min-w-0"
            onConfirm={onConfirm}
          />
        ) : (
          <span
            className={cn(
              "flex min-h-11 items-center justify-end px-1 text-sm tabular-nums @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:text-xs",
              priority === "primary" && "font-semibold text-foreground",
              priority === "secondary" && "text-muted-foreground",
              invalid && "text-destructive",
            )}
          >
            {displayValue}
          </span>
        )}
      </BlockerTooltip>
    </DropshipLineValue>
  );
}

function minimumSellPrice(issue: DropshipV2Issue) {
  const value = (issue.details as Record<string, unknown> | undefined)
    ?.minimum_sell_price;
  if (typeof value === "string" && value) return value;
  // Blockers raised before the price was stored only carry it in their message.
  return /at least \$(\d+(?:\.\d+)?)/.exec(issue.message)?.[1] ?? null;
}

function blockerMessage(issue: DropshipV2Issue) {
  if (issue.code === "minimum_margin_violation") {
    const minimum = minimumSellPrice(issue);
    return minimum
      ? `Sell must be at least ${formatMoney(minimum)} for the minimum margin`
      : "Sell is below the minimum margin";
  }
  if (issue.code === "model_missing") return "Add a model or item label";
  return issue.message;
}

/** The red field explains its blocker on hover, and on focus when "Review blocker" lands on it. */
function BlockerTooltip({
  message,
  children,
}: {
  message?: string;
  children: ReactNode;
}) {
  if (!message) return children;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div data-line-blocker className="min-w-0">
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={4}
          className="max-w-64 bg-destructive text-white [&_svg]:bg-destructive [&_svg]:fill-destructive"
        >
          {message}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const BLOCKER_FIELDS = new Set([
  "model",
  "quantity",
  "sell_price",
  "net_price",
]);

function LineRow({
  line,
  group,
  readOnly,
  expanded = false,
  contentId,
  busy,
  removing,
  renderManufacturer,
  renderSource,
  onToggleGroup,
  onEdit,
  onAddChild,
  reviewMatchAvailable,
  onReviewMatch,
  onRemove,
  blockers = [],
}: {
  line: DropshipV2Line;
  group?: DropshipV2LineGroup;
  readOnly: boolean;
  expanded?: boolean;
  contentId?: string;
  busy: boolean;
  removing: boolean;
  renderManufacturer?: (line: DropshipV2Line) => ReactNode;
  renderSource?: (line: DropshipV2Line) => ReactNode;
  onToggleGroup?: () => void;
  onEdit: (line: DropshipV2Line, changes: DropshipV2LineChanges) => void;
  onAddChild: (line: DropshipV2Line) => void;
  reviewMatchAvailable: boolean;
  onReviewMatch?: (line: DropshipV2Line) => void;
  onRemove: (line: DropshipV2Line) => void;
  blockers?: DropshipV2Issue[];
}) {
  const lineLabel = `Line ${line.display_number}`;
  const child = !group;
  const collapsible = Boolean(group?.children.length);
  const editable = !readOnly && (child || expanded || !collapsible);
  const selectedConfiguration =
    line.line_kind === "configuration" &&
    line.pricing_source === "config_accessory_v4_explicit_zero";
  const productIdentityEditable =
    editable && line.product_id == null && !selectedConfiguration;
  // Each blocker marks the field it is about; anything else marks the line number.
  const blockerText = (field: string) =>
    blockers
      .filter((issue) =>
        field === "line"
          ? !BLOCKER_FIELDS.has(issue.field_key ?? "")
          : issue.field_key === field,
      )
      .map(blockerMessage)
      .join(" · ") || undefined;
  const lineBlocker = blockerText("line");
  const modelBlocker = blockerText("model");
  const quantityBlocker = blockerText("quantity");

  return (
    <DropshipLineRowShell
      child={child}
      id={`dropship-line-${line.id}`}
      data-dropship-equipment-row={group ? "parent" : "child"}
      tabIndex={-1}
      className={cn(
        "outline-none transition-colors focus-visible:bg-accent/40",
      )}
    >
      <DropshipLineControlBand child={child}>
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-wrap items-center gap-1.5 @4xl/dropship-lines:gap-2",
            child && "pl-3",
          )}
        >
          {group && collapsible ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-11 shrink-0 @4xl/dropship-lines:size-8"
              aria-expanded={expanded}
              aria-controls={contentId}
              aria-label={`${expanded ? "Collapse" : "Expand"} equipment group ${line.display_number}: ${line.model || "untitled item"}`}
              onClick={onToggleGroup}
            >
              {expanded ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          ) : group ? (
            <span
              className="size-11 shrink-0 @4xl/dropship-lines:size-8"
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
          <BlockerTooltip message={lineBlocker}>
            {!readOnly ? (
              <POInlineEditableField
                value={line.display_number}
                displayValue={line.display_number}
                ariaLabel={`Edit ${lineLabel} number`}
                title={lineBlocker ? "" : undefined}
                pending={busy}
                invalid={Boolean(lineBlocker)}
                buttonClassName={cn(
                  "min-h-11 min-w-11 shrink-0 justify-start px-1 text-xs font-semibold text-muted-foreground @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:min-w-8",
                  lineBlocker && "text-destructive",
                )}
                inputClassName="w-20 min-w-20 text-xs"
                onConfirm={(value) =>
                  onEdit(line, editableValue("display_number", value))
                }
              />
            ) : (
              <span
                className={cn(
                  "shrink-0 text-xs font-semibold text-muted-foreground",
                  lineBlocker && "text-destructive",
                )}
              >
                {line.display_number}
              </span>
            )}
          </BlockerTooltip>
          {child && selectedConfiguration ? (
            <Badge variant="outline">Selected configuration</Badge>
          ) : child && readOnly ? (
            <LineChoiceValue
              value={line.line_kind as LineKind}
              options={LINE_KIND_OPTIONS}
              ariaLabel={`${lineLabel} sub-line type`}
            />
          ) : child ? (
            <LineChoiceMenu
              value={line.line_kind as LineKind}
              options={LINE_KIND_OPTIONS}
              ariaLabel={`${lineLabel} sub-line type`}
              busy={busy}
              onChange={(value) => onEdit(line, { line_kind: value })}
            />
          ) : null}
          {/* A warranty sub-line can name its own provider (e.g. Safeware), which gets its own PO. */}
          {group || line.line_kind === "warranty" ? (
            <div data-line-order-manufacturer className="min-w-0 max-w-full">
              {productIdentityEditable && renderManufacturer ? (
                renderManufacturer(line)
              ) : (
                <span className="block min-w-0 max-w-full truncate text-sm font-semibold text-foreground">
                  {line.manufacturer_name || "Choose manufacturer"}
                </span>
              )}
            </div>
          ) : null}
          {/* Ordered from our own warehouse instead of the manufacturer (USA-1574). */}
          {group && renderSource ? (
            renderSource(line)
          ) : group && line.warehouse_source ? (
            <StatusPill tone="progress">
              Our warehouse · {line.warehouse_source.label}
            </StatusPill>
          ) : null}
          {line.product_id == null && !selectedConfiguration ? (
            <Badge variant="secondary">
              {line.custom_item ? "Custom" : "Unmatched"}
            </Badge>
          ) : null}
        </div>
        {readOnly ? (
          <LineChoiceValue
            value={line.disposition as LineDisposition}
            options={LINE_DISPOSITION_OPTIONS}
            ariaLabel={`${lineLabel} PO treatment`}
            fallbackLabels={LEGACY_LINE_DISPOSITION_LABELS}
            warning={line.disposition !== "include_on_po"}
          />
        ) : (
          <LineChoiceMenu
            value={line.disposition as LineDisposition}
            options={LINE_DISPOSITION_OPTIONS}
            ariaLabel={`${lineLabel} PO treatment`}
            fallbackLabels={LEGACY_LINE_DISPOSITION_LABELS}
            busy={busy}
            warning={line.disposition !== "include_on_po"}
            align="end"
            onChange={(value) =>
              onEdit(line, {
                disposition: value,
                disposition_reason: null,
              })
            }
          />
        )}
      </DropshipLineControlBand>

      <DropshipLineDataGrid>
        <div className={cn("min-w-0", child && "pl-4")}>
          <POLineItemProductCell
            model={line.model || "Untitled item"}
            description={line.description}
            sku={line.sku}
            productUuid={line.product_uuid}
            imageUrl={line.product_image_url}
            manufacturerName={line.manufacturer_name}
            showManufacturer={false}
            compact
            inlineSku
            className="items-start gap-2"
            modelContent={
              productIdentityEditable ? (
                <BlockerTooltip message={modelBlocker}>
                  <POInlineEditableField
                    value={line.model}
                    placeholder="Add model"
                    ariaLabel={`Edit ${lineLabel} model`}
                    title={modelBlocker ? "" : undefined}
                    pending={busy}
                    invalid={Boolean(modelBlocker)}
                    buttonClassName={cn(
                      "min-h-11 max-w-full justify-start px-0 text-left font-medium text-primary @4xl/dropship-lines:min-h-8",
                      modelBlocker && "px-2 text-destructive",
                    )}
                    inputClassName="min-w-44"
                    onConfirm={(value) =>
                      onEdit(line, editableValue("model", value))
                    }
                  />
                </BlockerTooltip>
              ) : undefined
            }
            descriptionContent={
              productIdentityEditable ? (
                <POInlineEditableField
                  value={line.description}
                  placeholder="Add description"
                  ariaLabel={`Edit ${lineLabel} description`}
                  pending={busy}
                  buttonClassName="min-h-11 w-full justify-start px-0 py-0 text-left text-sm text-muted-foreground @4xl/dropship-lines:min-h-6 @4xl/dropship-lines:text-xs"
                  inputClassName="min-w-56"
                  onConfirm={(value) =>
                    onEdit(line, editableValue("description", value))
                  }
                />
              ) : line.product_uuid && line.description ? (
                <ProductDetailsDialogTrigger
                  productUuid={line.product_uuid}
                  model={line.model || "Catalog product"}
                  description={line.description}
                />
              ) : undefined
            }
          />
        </div>

        <DropshipLineNumericGrid>
          <DropshipLineValue label="QTY">
            <BlockerTooltip message={quantityBlocker}>
              {editable ? (
                <POInlineEditableField
                  value={line.quantity}
                  displayValue={String(line.quantity ?? 0)}
                  kind="number"
                  align="right"
                  ariaLabel={`Edit ${lineLabel} quantity`}
                  title={quantityBlocker ? "" : undefined}
                  pending={busy}
                  invalid={Boolean(quantityBlocker)}
                  buttonClassName={cn(
                    "ml-auto min-h-11 min-w-0 w-full justify-end px-1 text-sm tabular-nums @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:text-xs",
                    quantityBlocker && "text-destructive",
                  )}
                  inputClassName="w-full min-w-0"
                  onConfirm={(value) =>
                    onEdit(line, editableValue("quantity", value))
                  }
                />
              ) : (
                <span
                  className={cn(
                    "flex min-h-11 items-center justify-end px-1 text-sm tabular-nums @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:text-xs",
                    quantityBlocker && "text-destructive",
                  )}
                >
                  {String(line.quantity ?? 0)}
                </span>
              )}
            </BlockerTooltip>
          </DropshipLineValue>
          <PriceEditor
            label="SELL"
            value={line.sell_price}
            displayValue={amount(line.sell_price)}
            ariaLabel={`Edit ${lineLabel} customer sell`}
            busy={busy}
            editable={editable}
            priority="primary"
            blocker={blockerText("sell_price")}
            onConfirm={(value) =>
              onEdit(line, editableValue("sell_price", value))
            }
          />
          <PriceEditor
            label="NET"
            value={line.net_price}
            displayValue={amount(line.net_price)}
            ariaLabel={`Edit ${lineLabel} supplier net`}
            busy={busy}
            editable={editable}
            priority="primary"
            blocker={blockerText("net_price")}
            onConfirm={(value) =>
              onEdit(line, editableValue("net_price", value))
            }
          />
          <PriceEditor
            label="DISCOUNT"
            value={line.discount_chain}
            displayValue={line.discount_chain?.trim() || "—"}
            ariaLabel={`Edit ${lineLabel} discount chain`}
            busy={busy}
            editable={editable}
            kind="text"
            onConfirm={(value) =>
              onEdit(line, editableValue("discount_chain", value))
            }
          />
          <PriceEditor
            label="LIST"
            value={line.list_price}
            displayValue={amount(line.list_price)}
            ariaLabel={`Edit ${lineLabel} list price`}
            busy={busy}
            editable={editable}
            priority="secondary"
            onConfirm={(value) =>
              onEdit(line, editableValue("list_price", value))
            }
          />
          <DropshipLineValue label="SELL TOTAL" className="text-xs font-medium">
            <span
              className="flex min-h-11 items-center justify-end px-1 text-sm tabular-nums @4xl/dropship-lines:min-h-8 @4xl/dropship-lines:text-xs"
              aria-label={`${lineLabel} total`}
            >
              {amount(line.line_total)}
            </span>
          </DropshipLineValue>
        </DropshipLineNumericGrid>

        <DropshipLineActions>
          {!child && reviewMatchAvailable && onReviewMatch ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-11 @4xl/dropship-lines:size-8"
              aria-label={`Review AI match for ${lineLabel}`}
              title="Review AI match"
              disabled={busy || removing}
              onClick={() => onReviewMatch(line)}
            >
              <QuoteAgentLineActionIcon />
            </Button>
          ) : null}
          {!readOnly && !child ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-11 @4xl/dropship-lines:size-8"
              aria-label={`Add sub-line under ${lineLabel}`}
              title="Add sub-line"
              disabled={busy || removing}
              onClick={() => onAddChild(line)}
            >
              <Plus className="size-4" />
            </Button>
          ) : null}
          {!readOnly ? (
            <ActionPopover
              align="end"
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-11 text-muted-foreground hover:bg-destructive/10 hover:text-destructive @4xl/dropship-lines:size-8"
                  aria-label={`Remove ${lineLabel}`}
                  title={
                    child
                      ? "Remove sub-line"
                      : "Remove equipment and its sub-lines"
                  }
                  disabled={busy || removing}
                >
                  {removing ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Trash2 className="size-4" />
                  )}
                </Button>
              }
              title={
                line.depth === 0
                  ? "Remove equipment group?"
                  : "Remove sub-line?"
              }
              confirmLabel="Remove line"
              confirmVariant="destructive"
              onConfirm={(_note, close) => {
                onRemove(line);
                close();
              }}
            >
              <p className="text-xs text-muted-foreground">
                {line.depth === 0
                  ? `Line ${line.display_number} and ${group?.children.length ?? 0} attached sub-lines will be removed together. Totals and approvals will be recalculated.`
                  : `Line ${line.display_number} will be removed from its parent. Totals and approvals will be recalculated.`}
              </p>
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="font-medium">
                  {line.model || "Untitled item"}
                </span>
                {line.description ? (
                  <span className="mt-1 line-clamp-3 block text-muted-foreground">
                    {line.description}
                  </span>
                ) : null}
              </div>
            </ActionPopover>
          ) : null}
        </DropshipLineActions>

        <div
          data-internal-note
          className={cn(
            "min-w-0 @4xl/dropship-lines:col-span-full",
            line.internal_note &&
              "rounded-md border border-amber-300 bg-amber-50/60 px-2 py-1.5 dark:border-amber-700 dark:bg-amber-950/20",
          )}
        >
          {line.internal_note ? (
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Internal note
            </span>
          ) : null}
          {readOnly ? (
            <span
              className={cn(
                "block min-h-8 whitespace-pre-wrap py-1 text-xs leading-5",
                !line.internal_note && "text-muted-foreground",
              )}
            >
              {line.internal_note || "—"}
            </span>
          ) : (
            <POInlineEditableField
              value={line.internal_note}
              displayValue={line.internal_note ?? ""}
              placeholder="-"
              kind="textarea"
              allowWrap
              showEditIcon
              ariaLabel={`Edit ${lineLabel} internal note`}
              pending={busy}
              buttonClassName={cn(
                "min-h-8 justify-start whitespace-pre-wrap px-0 py-0 text-left text-xs leading-5",
                line.internal_note
                  ? "w-full"
                  : "w-auto text-muted-foreground",
              )}
              inputClassName="min-h-24 w-full min-w-64 whitespace-pre-wrap"
              onConfirm={(value) =>
                onEdit(line, editableValue("internal_note", value))
              }
            />
          )}
        </div>
      </DropshipLineDataGrid>
    </DropshipLineRowShell>
  );
}

/**
 * Shared dense hierarchy surface for AQProj review. Parent equipment and
 * arbitrary priced sub-lines stay visually inseparable and expose the same
 * click-to-edit behavior used by PO Details.
 */
export function DropshipLineItemTable({
  groups,
  sortMode,
  readOnly = false,
  loadedRootCount,
  totalRootCount,
  hasMore,
  loadingMore,
  loadMoreError = false,
  manufacturerLoading = false,
  pendingLineUuids,
  removingLineUuid,
  revealLineId,
  revealNonce,
  revealManufacturerKey,
  lineBlockers,
  emptyState,
  renderManufacturer,
  renderManufacturerDetails,
  renderSource,
  onSortModeChange,
  onEdit,
  onAddRoot,
  onAddChild,
  reviewableMatchLineUuids,
  onReviewMatch,
  onRemove,
  onLoadMore,
}: {
  groups: DropshipV2LineGroup[];
  sortMode: DropshipLineSortMode;
  readOnly?: boolean;
  loadedRootCount: number;
  totalRootCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError?: boolean;
  manufacturerLoading?: boolean;
  pendingLineUuids?: ReadonlySet<string>;
  removingLineUuid?: string;
  revealLineId?: number;
  /** Changes on every reveal request so repeated clicks on one line still scroll to it. */
  revealNonce?: number;
  revealManufacturerKey?: string;
  /** Open blocking validation issues keyed by line id. */
  lineBlockers?: ReadonlyMap<number, DropshipV2Issue[]>;
  emptyState?: ReactNode;
  renderManufacturer?: (line: DropshipV2Line) => ReactNode;
  renderManufacturerDetails?: (
    bucket: DropshipManufacturerBucketSummary,
  ) => ReactNode;
  /** Where a top-level line is ordered from: its manufacturer or our warehouse (USA-1574). */
  renderSource?: (line: DropshipV2Line) => ReactNode;
  onSortModeChange: (mode: DropshipLineSortMode) => void;
  onEdit: (line: DropshipV2Line, changes: DropshipV2LineChanges) => void;
  onAddRoot: () => void;
  onAddChild: (line: DropshipV2Line) => void;
  reviewableMatchLineUuids?: ReadonlySet<string>;
  onReviewMatch?: (line: DropshipV2Line) => void;
  onRemove: (line: DropshipV2Line) => void;
  onLoadMore: () => void;
}) {
  const [expandedGroupUuids, setExpandedGroupUuids] = useState<Set<string>>(
    () => new Set(groups[0] ? [groups[0].root.uuid] : []),
  );
  const [expandedManufacturerKeys, setExpandedManufacturerKeys] = useState<
    Set<string>
  >(() => new Set());
  const initializedExpansion = useRef(groups.length > 0);
  const manufacturerBuckets = useMemo(
    () => groupByManufacturer(groups),
    [groups],
  );
  useEffect(() => {
    const currentGroupUuids = new Set(groups.map((group) => group.root.uuid));
    setExpandedGroupUuids((current) => {
      const next = new Set(
        [...current].filter((uuid) => currentGroupUuids.has(uuid)),
      );
      if (!initializedExpansion.current && groups[0]) {
        next.add(groups[0].root.uuid);
        initializedExpansion.current = true;
      }
      if (
        next.size === current.size &&
        [...next].every((uuid) => current.has(uuid))
      )
        return current;
      return next;
    });
  }, [groups]);

  // Groups holding a blocker open once so the red line is visible without hunting.
  const autoExpandedBlockerGroups = useRef(new Set<string>());
  useEffect(() => {
    if (!lineBlockers?.size) return;
    const blockerGroupUuids = groups
      .filter(
        (group) =>
          !autoExpandedBlockerGroups.current.has(group.root.uuid) &&
          [group.root, ...group.children].some((line) =>
            lineBlockers.has(line.id),
          ),
      )
      .map((group) => group.root.uuid);
    if (!blockerGroupUuids.length) return;
    for (const uuid of blockerGroupUuids)
      autoExpandedBlockerGroups.current.add(uuid);
    const expandTimer = window.setTimeout(() => {
      setExpandedGroupUuids(
        (current) => new Set([...current, ...blockerGroupUuids]),
      );
    }, 0);
    return () => window.clearTimeout(expandTimer);
  }, [groups, lineBlockers]);

  const [pendingScrollLineId, setPendingScrollLineId] = useState<number>();
  // Each reveal request is handled once; later data refreshes must not scroll the page again.
  const [handledRevealKey, setHandledRevealKey] = useState<string>();
  useEffect(() => {
    const revealKey = `${revealLineId}:${revealNonce ?? 0}`;
    if (revealLineId == null || handledRevealKey === revealKey) return;
    const targetGroup = groups.find(
      (group) =>
        group.root.id === revealLineId ||
        group.children.some((line) => line.id === revealLineId),
    );
    if (!targetGroup) return;
    const bucketKey =
      sortMode === "manufacturer"
        ? manufacturerBuckets.find((bucket) =>
            bucket.groups.some(
              (group) => group.root.uuid === targetGroup.root.uuid,
            ),
          )?.key
        : undefined;
    const revealTimer = window.setTimeout(() => {
      setHandledRevealKey(revealKey);
      setExpandedGroupUuids((current) => {
        if (current.has(targetGroup.root.uuid)) return current;
        return new Set([...current, targetGroup.root.uuid]);
      });
      if (bucketKey) {
        setExpandedManufacturerKeys((current) =>
          current.has(bucketKey) ? current : new Set([...current, bucketKey]),
        );
      }
      setPendingScrollLineId(revealLineId);
    }, 0);
    return () => window.clearTimeout(revealTimer);
  }, [
    groups,
    handledRevealKey,
    manufacturerBuckets,
    revealLineId,
    revealNonce,
    sortMode,
  ]);

  // Scroll once the expanded group (and manufacturer section) has rendered the row, then
  // focus its red field after scrolling settles: the explanation closes on any scroll.
  useEffect(() => {
    if (pendingScrollLineId == null) return;
    const element = document.getElementById(
      `dropship-line-${pendingScrollLineId}`,
    );
    if (!element) return;
    const target =
      element.querySelector<HTMLElement>(
        "[data-line-blocker] button, [data-line-blocker] [tabindex]",
      ) ?? element;
    let settleTimer = 0;
    const settle = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        window.removeEventListener("scroll", settle, true);
        target.focus({ preventScroll: true });
        setPendingScrollLineId(undefined);
      }, 150);
    };
    window.addEventListener("scroll", settle, true);
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    settle();
    return () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener("scroll", settle, true);
    };
  }, [pendingScrollLineId, expandedGroupUuids, expandedManufacturerKeys]);

  useEffect(() => {
    if (
      !revealManufacturerKey ||
      !manufacturerBuckets.some(
        (bucket) => bucket.key === revealManufacturerKey,
      )
    ) {
      return;
    }
    const revealTimer = window.setTimeout(() => {
      setExpandedManufacturerKeys((current) =>
        current.has(revealManufacturerKey)
          ? current
          : new Set([...current, revealManufacturerKey]),
      );
    }, 0);
    return () => window.clearTimeout(revealTimer);
  }, [manufacturerBuckets, revealManufacturerKey]);

  const toggleGroup = (uuid: string) =>
    setExpandedGroupUuids((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const renderEquipmentGroup = (group: DropshipV2LineGroup) => {
    const expanded =
      group.children.length === 0 || expandedGroupUuids.has(group.root.uuid);
    const contentId = `dropship-group-content-${group.root.id}`;
    return (
      <section key={group.root.uuid} id={`dropship-group-${group.root.id}`}>
        <LineRow
          line={group.root}
          group={group}
          readOnly={readOnly}
          expanded={expanded}
          contentId={contentId}
          busy={pendingLineUuids?.has(group.root.uuid) ?? false}
          removing={removingLineUuid === group.root.uuid}
          renderManufacturer={renderManufacturer}
          renderSource={renderSource}
          onToggleGroup={
            group.children.length
              ? () => toggleGroup(group.root.uuid)
              : undefined
          }
          onEdit={onEdit}
          onAddChild={onAddChild}
          reviewMatchAvailable={
            reviewableMatchLineUuids?.has(group.root.uuid) ?? false
          }
          onReviewMatch={onReviewMatch}
          onRemove={onRemove}
          blockers={lineBlockers?.get(group.root.id)}
        />
        {expanded && group.children.length ? (
          <div id={contentId}>
            {group.children.map((child) => (
              <LineRow
                key={child.uuid}
                line={child}
                readOnly={readOnly}
                busy={pendingLineUuids?.has(child.uuid) ?? false}
                removing={removingLineUuid === child.uuid}
                renderManufacturer={renderManufacturer}
                onEdit={onEdit}
                onAddChild={onAddChild}
                reviewMatchAvailable={false}
                onRemove={onRemove}
                blockers={lineBlockers?.get(child.id)}
              />
            ))}
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <section
      aria-labelledby="equipment-lines-title"
      className="@container/dropship-lines overflow-hidden rounded-xl border bg-background"
    >
      <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h2 id="equipment-lines-title" className="text-base font-semibold">
            Equipment and configuration
          </h2>
          <Badge
            variant="outline"
            aria-label={`${loadedRootCount} of ${totalRootCount} equipment groups loaded`}
          >
            {hasMore
              ? `${loadedRootCount} / ${totalRootCount}`
              : totalRootCount}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Line order"
            className="inline-flex rounded-md border bg-muted/30 p-0.5"
          >
            <Button
              type="button"
              size="sm"
              className="min-h-11 @4xl/dropship-lines:min-h-8"
              variant={sortMode === "quote" ? "secondary" : "ghost"}
              aria-pressed={sortMode === "quote"}
              onClick={() => onSortModeChange("quote")}
            >
              Quote order
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-h-11 @4xl/dropship-lines:min-h-8"
              variant={sortMode === "manufacturer" ? "secondary" : "ghost"}
              aria-pressed={sortMode === "manufacturer"}
              onClick={() => onSortModeChange("manufacturer")}
            >
              Manufacturer groups
            </Button>
          </div>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              className="min-h-11 @4xl/dropship-lines:min-h-8"
              onClick={onAddRoot}
            >
              <PackagePlus className="size-4" /> Add equipment
            </Button>
          ) : null}
        </div>
      </div>

      {groups.length === 0 ? (
        (emptyState ?? (
          <div className="flex min-h-64 flex-col items-center justify-center px-6 py-12 text-center">
            <Boxes
              className="size-9 text-muted-foreground"
              aria-hidden="true"
            />
            <h3 className="mt-4 font-semibold">No equipment lines yet</h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {readOnly
                ? "No retained equipment is available."
                : "Add the first equipment item, then attach configuration, accessory, warranty, or informational sub-lines."}
            </p>
            {!readOnly ? (
              <Button type="button" className="mt-5" onClick={onAddRoot}>
                <Plus className="size-4" /> Add first equipment line
              </Button>
            ) : null}
          </div>
        ))
      ) : (
        <div>
          <DropshipLineColumnHeaders mode="sales" />
          {sortMode === "manufacturer" && manufacturerLoading ? (
            <div className="flex min-h-40 flex-col items-center justify-center gap-2 border-t px-6 py-10 text-center">
              <Loader2
                className="size-5 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-sm font-medium">
                Loading all manufacturer groups
              </p>
              <p className="text-xs text-muted-foreground">
                {loadedRootCount} of {totalRootCount} equipment groups loaded
              </p>
            </div>
          ) : sortMode === "manufacturer" ? (
            manufacturerBuckets.map((bucket) => {
              const expanded = expandedManufacturerKeys.has(bucket.key);
              const detailsId = `dropship-manufacturer-details-${bucket.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
              const discount =
                bucket.discounts.size === 1
                  ? ([...bucket.discounts][0] ?? "—")
                  : "Mixed";
              return (
                <section key={bucket.key} data-manufacturer-group>
                  <div className="border-t bg-muted/20">
                    <DropshipLineDataGrid className="items-center py-3">
                      <button
                        type="button"
                        className="flex min-h-11 min-w-0 items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        onClick={() =>
                          setExpandedManufacturerKeys((current) => {
                            const next = new Set(current);
                            if (next.has(bucket.key)) next.delete(bucket.key);
                            else next.add(bucket.key);
                            return next;
                          })
                        }
                      >
                        {expanded ? (
                          <ChevronDown className="size-4 shrink-0" />
                        ) : (
                          <ChevronRight className="size-4 shrink-0" />
                        )}
                        <Building2 className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold">
                            {bucket.name}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {bucket.groups.length} equipment group
                            {bucket.groups.length === 1 ? "" : "s"}
                          </span>
                        </span>
                      </button>
                      <DropshipLineNumericGrid>
                        <DropshipLineValue label="QTY">
                          <span className="tabular-nums">
                            {bucket.quantity}
                          </span>
                        </DropshipLineValue>
                        <DropshipLineValue label="SELL">
                          <span className="tabular-nums">
                            {formatMoney(bucket.sell.toFixed(2))}
                          </span>
                        </DropshipLineValue>
                        <DropshipLineValue label="NET">
                          <span className="tabular-nums">
                            {formatMoney(bucket.net.toFixed(2))}
                          </span>
                        </DropshipLineValue>
                        <DropshipLineValue label="DISCOUNT">
                          <span>{discount}</span>
                        </DropshipLineValue>
                        <DropshipLineValue label="LIST">
                          <span className="tabular-nums">
                            {formatMoney(bucket.list.toFixed(2))}
                          </span>
                        </DropshipLineValue>
                        <DropshipLineValue label="SELL TOTAL">
                          <span className="font-medium tabular-nums">
                            {formatMoney(bucket.sellTotal.toFixed(2))}
                          </span>
                        </DropshipLineValue>
                      </DropshipLineNumericGrid>
                      <DropshipLineActions>
                        <span className="size-8" aria-hidden="true" />
                      </DropshipLineActions>
                    </DropshipLineDataGrid>
                  </div>
                  {expanded ? (
                    <div id={detailsId} className="border-t bg-background">
                      {renderManufacturerDetails ? (
                        <div data-manufacturer-details>
                          {renderManufacturerDetails({
                            key: bucket.key,
                            name: bucket.name,
                            manufacturerId: bucket.manufacturerId,
                            rootLineUuids: bucket.rootLineUuids,
                          })}
                        </div>
                      ) : null}
                      {bucket.groups.map(renderEquipmentGroup)}
                    </div>
                  ) : null}
                </section>
              );
            })
          ) : (
            groups.map(renderEquipmentGroup)
          )}
          {loadMoreError ? (
            <div
              role="alert"
              className="flex flex-col gap-3 border-t border-destructive/30 bg-destructive/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <p className="text-sm font-medium text-destructive">
                  Only part of the equipment list is available
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {loadedRootCount} of {totalRootCount} equipment groups loaded.
                  Retry the missing page before approval.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 shrink-0 sm:min-h-9"
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {loadingMore ? (
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                ) : null}
                {loadingMore
                  ? "Retrying equipment groups..."
                  : "Retry loading equipment groups"}
              </Button>
            </div>
          ) : hasMore && !manufacturerLoading ? (
            <div
              role="status"
              className="flex min-h-11 items-center justify-center gap-2 border-t px-4 py-3 text-xs text-muted-foreground"
            >
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Loading remaining equipment groups ({loadedRootCount} of{" "}
              {totalRootCount})
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
